// Web counterpart of tempdash-worker/components/LegalText.jsx —
// renders the same markdown subset (# / ## / ### / blockquotes /
// bullets / **bold** / *italic* / inline links) using Tailwind
// classes that sit consistently with the Practice dashboard’s
// type scale.
//
// Exported as the default — drop-in for /legal/* pages.

export default function LegalText({ source }) {
  if (!source) return null
  const lines = source.split('\n')
  const blocks = []
  let i = 0
  let key = 0
  const k = () => `b${key++}`

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim() === '') { i++; continue }

    if (line.startsWith('### ')) {
      blocks.push(<h4 key={k()} className="mt-6 mb-2 text-[17px] font-semibold text-ink">{renderInline(line.slice(4))}</h4>)
      i++; continue
    }
    if (line.startsWith('## ')) {
      blocks.push(<h3 key={k()} className="mt-8 mb-3 text-[20px] font-bold tracking-[-0.01em] text-ink">{renderInline(line.slice(3))}</h3>)
      i++; continue
    }
    if (line.startsWith('# ')) {
      // ⛔ h2, not h1. LegalDocument.jsx already renders the page’s one <h1>;
      // a document that brings its own gave every /legal/* page two, which is
      // invalid and splits the page’s topic for a crawler.
      blocks.push(<h2 key={k()} className="mt-4 mb-4 text-[26px] font-bold tracking-[-0.01em] text-ink">{renderInline(line.slice(2))}</h2>)
      i++; continue
    }

    if (line.startsWith('>')) {
      const quoteLines = []
      while (i < lines.length && (lines[i].startsWith('>') || (quoteLines.length > 0 && lines[i].trim() === ''))) {
        const cleaned = lines[i].replace(/^>\s?/, '')
        quoteLines.push(cleaned)
        i++
      }
      blocks.push(
        <blockquote key={k()} className="my-4 rounded-tile border-l-4 border-action bg-action-tint px-4 py-3 text-[16px] font-medium text-ink">
          {renderInline(quoteLines.join('\n').trim())}
        </blockquote>,
      )
      continue
    }

    if (/^[-*]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''))
        i++
      }
      blocks.push(
        <ul key={k()} className="mb-3 ml-2 list-disc space-y-1 pl-5 text-[17px] leading-relaxed text-ink-muted">
          {items.map((item, idx) => <li key={idx}>{renderInline(item)}</li>)}
        </ul>,
      )
      continue
    }

    const paraLines = [line]
    i++
    while (i < lines.length && lines[i].trim() !== '' &&
           !lines[i].startsWith('#') &&
           !lines[i].startsWith('>') &&
           !/^[-*]\s+/.test(lines[i])) {
      paraLines.push(lines[i])
      i++
    }
    blocks.push(
      <p key={k()} className="mb-3 text-[17px] leading-relaxed text-ink-muted">
        {renderInline(paraLines.join(' '))}
      </p>,
    )
  }

  return <div className="legal-prose">{blocks}</div>
}

function renderInline(input) {
  const out = []
  let i = 0
  let buf = ''
  let keyN = 0
  const flush = () => { if (buf) { out.push(buf); buf = '' } }
  while (i < input.length) {
    if (input.startsWith('**', i)) {
      const end = input.indexOf('**', i + 2)
      if (end > -1) {
        flush()
        out.push(<strong key={`b${keyN++}`} className="font-semibold text-ink">{input.slice(i + 2, end)}</strong>)
        i = end + 2
        continue
      }
    }
    if (input[i] === '*' && input[i + 1] !== '*' && input[i - 1] !== '*') {
      const end = input.indexOf('*', i + 1)
      if (end > -1) {
        flush()
        out.push(<em key={`i${keyN++}`}>{input.slice(i + 1, end)}</em>)
        i = end + 1
        continue
      }
    }
    if (input.startsWith('http://', i) || input.startsWith('https://', i)) {
      const m = input.slice(i).match(/^(https?:\/\/[^\s)]+)/)
      if (m) {
        flush()
        const url = m[1]
        out.push(
          <a key={`l${keyN++}`} href={url} target="_blank" rel="noopener noreferrer"
             className="font-semibold text-link underline">
            {url}
          </a>,
        )
        i += url.length
        continue
      }
    }
    buf += input[i]
    i += 1
  }
  flush()
  return out
}
